'use client';

import { useState } from 'react';
import { GitCommit, User, Clock, FileText, Plus, Minus } from 'lucide-react';
import { recentCommit, Change } from './mockData';

export function ChangesTab() {
  const [selectedFile, setSelectedFile] = useState<string>(recentCommit.changes[0]?.file || '');

  const selectedChange = recentCommit.changes.find((c) => c.file === selectedFile);

  return (
    <div className="p-4">
      {/* Commit Info */}
      <div className="mb-4 p-4 bg-bg-card rounded-lg border border-border/50">
        <div className="flex items-start gap-3">
          {/* Commit Icon */}
          <div className="p-2 bg-primary/20 rounded-lg">
            <GitCommit className="w-5 h-5 text-primary" />
          </div>

          <div className="flex-1 min-w-0">
            {/* Commit Message */}
            <h3 className="text-text font-medium mb-2">{recentCommit.message}</h3>

            {/* Commit Meta */}
            <div className="flex items-center gap-4 text-text-muted text-sm flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-primary">{recentCommit.hash}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                <span>{recentCommit.author}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>{recentCommit.timestamp}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* File List */}
        <div className="lg:col-span-1">
          <div className="bg-bg-card rounded-lg border border-border/50 overflow-hidden">
            <div className="px-3 py-2 border-b border-border/30">
              <span className="text-text-secondary text-sm font-medium">Changed Files</span>
            </div>
            <div className="p-2 space-y-1">
              {recentCommit.changes.map((change) => (
                <FileItem
                  key={change.file}
                  change={change}
                  selected={change.file === selectedFile}
                  onClick={() => setSelectedFile(change.file)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Diff View */}
        <div className="lg:col-span-3">
          <div className="bg-bg-card rounded-lg border border-border/50 overflow-hidden">
            {/* File Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-border/30 bg-bg-alt/50">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-text-muted" />
                <span className="font-mono text-sm text-text">{selectedFile}</span>
              </div>
              {selectedChange && (
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-green-400 flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" />
                    {selectedChange.additions}
                  </span>
                  <span className="text-red-400 flex items-center gap-1">
                    <Minus className="w-3.5 h-3.5" />
                    {selectedChange.deletions}
                  </span>
                </div>
              )}
            </div>

            {/* Diff Content */}
            <div className="overflow-x-auto">
              {selectedChange && <DiffView diff={selectedChange.diff} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface FileItemProps {
  change: Change;
  selected: boolean;
  onClick: () => void;
}

function FileItem({ change, selected, onClick }: FileItemProps) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
        selected
          ? 'bg-primary/20 border border-primary/30'
          : 'hover:bg-white/5 border border-transparent'
      }`}
    >
      <FileText className={`w-4 h-4 ${selected ? 'text-primary' : 'text-text-muted'}`} />
      <span className={`flex-1 font-mono text-sm truncate ${selected ? 'text-text' : 'text-text-secondary'}`}>
        {change.file}
      </span>
      <div className="flex items-center gap-1 text-xs">
        <span className="text-green-400">+{change.additions}</span>
        <span className="text-red-400">-{change.deletions}</span>
      </div>
    </button>
  );
}

interface DiffViewProps {
  diff: string;
}

function DiffView({ diff }: DiffViewProps) {
  const lines = diff.split('\n');

  return (
    <div className="font-mono text-sm">
      {lines.map((line, index) => {
        let lineClass = 'text-text-secondary';
        let bgClass = '';
        let prefix = ' ';

        if (line.startsWith('@@')) {
          lineClass = 'text-cyan-400';
          bgClass = 'bg-cyan-500/10';
        } else if (line.startsWith('+')) {
          lineClass = 'text-green-400';
          bgClass = 'bg-green-500/10';
          prefix = '+';
        } else if (line.startsWith('-')) {
          lineClass = 'text-red-400';
          bgClass = 'bg-red-500/10';
          prefix = '-';
        }

        // For lines starting with +/-, strip the prefix for display
        const displayLine = line.startsWith('+') || line.startsWith('-') ? line.slice(1) : line;
        const showPrefix = line.startsWith('+') || line.startsWith('-');

        return (
          <div key={index} className={`flex ${bgClass}`}>
            {/* Line Number */}
            <div className="w-12 px-2 py-0.5 text-right text-text-muted/50 border-r border-border/30 select-none shrink-0">
              {index + 1}
            </div>

            {/* Prefix */}
            <div className={`w-6 px-1 py-0.5 text-center ${lineClass} select-none shrink-0`}>
              {showPrefix ? prefix : ''}
            </div>

            {/* Content */}
            <div className={`flex-1 px-2 py-0.5 ${lineClass} whitespace-pre`}>
              {displayLine}
            </div>
          </div>
        );
      })}
    </div>
  );
}
